/**
 * Member API Client
 * 與 POS 後端的 Member API 進行通訊
 */

import {
  getApiBase,
  getApiHeaders,
  getTenantIdFromUrl,
  apiFetch,
} from './apiUtils';
import type {
  OrderStatusValue,
  PaymentStatusValue,
  ShippingStatusValue,
} from '@pos/shared/constants';

interface UserByLineIdResponse {
  success: boolean;
  data: {
    id: number;
    name: string;
    points: number;
    points_yuan_equivalent: number;
    phone_verified?: boolean;
  };
  requestId?: string;
  timestamp: string;
}

/** 結帳偏好（供結帳表單預填） */
export interface CheckoutPreferencesResponse {
  success: true;
  data: {
    name: string | null;
    phone: string | null;
    email: string | null;
    carrierType: string | null;
    carrierNum: string | null;
    loveCode: string | null;
    defaultAddressSnapshot?: Record<string, unknown> | null;
  } | null;
}

interface UserDetailResponse {
  success: boolean;
  data: {
    id: number;
    line_id: string | null;
    name: string;
    phone: string | null;
    role: 'CLIENT' | 'ADMIN';
    is_active: number;
    points: number;
    points_yuan_equivalent: number;
    created_at: string;
    updated_at: string;
    last_purchase_at: string | null;
    current_month_spending: number;
    last_month_spending: number;
    phone_verified?: boolean;
    stats: {
      total_spent: number;
      last_purchase_at: string | null;
      total_orders: number;
    };
  };
  timestamp: string;
}

interface PointsTransaction {
  id: number;
  user_id: number;
  order_id: number | null;
  points_change: number;
  transaction_type: 'EARNED' | 'REDEEMED';
  balance_after: number;
  created_at: string;
}

interface PointsHistoryResponse {
  success: boolean;
  data: PointsTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

interface Order {
  id: number;
  order_number: string;
  user_id: number;
  /** 小計（分）；顯示請用 formatTwdFromCents */
  subtotal_twd: number;
  /** 折扣金額（分）；顯示請用 formatTwdFromCents */
  discount_twd: number;
  /** 點數折抵金額（分）；顯示請用 formatTwdFromCents */
  points_discount_twd: number;
  /** 訂單總額（分）；顯示請用 formatTwdFromCents */
  total_twd: number;
  status: 'created' | 'confirmed' | 'paid' | 'cancelled';
  order_status?: OrderStatusValue;
  payment_status?: PaymentStatusValue;
  /** 付款方式（如 pickup_pay、cvs_cod） */
  payment_method?: string | null;
  shipping_status?: ShippingStatusValue;
  /** 訂單管道：preorder＝面交預購；取貨驗證僅處理面交預購 */
  channel?: string;
  /** 面交/取貨日期（YYYY-MM-DD，僅預購有值） */
  pickup_date?: string | null;
  takeaway_number?: number | null;
  pickup_number?: string | null;
  created_at: string;
  updated_at: string;
  created_at_taipei: string;
  updated_at_taipei: string;
}

interface OrderListResponse {
  success: boolean;
  data: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

/**
 * 登入即加會員：依 LINE ID 取得或建立會員
 * @param tenantId 可選，若未提供則從 URL 取得
 */
export async function getOrCreateByLineId(
  lineId: string,
  displayName?: string,
  tenantId?: string
): Promise<UserByLineIdResponse> {
  const apiBase = getApiBase();
  const t = tenantId ?? getTenantIdFromUrl();
  const headers = getApiHeaders(t);

  const response = await apiFetch(
    `${apiBase}/api/customers/get-or-create-by-line-id`,
    {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ lineId, displayName }),
    }
  );

  if (!response.ok) {
    let errorMessage = '取得或建立會員失敗，請稍後再試';
    try {
      const error = (await response.json()) as { error?: string };
      errorMessage = error.error || errorMessage;
    } catch {
      /* ignore */
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as UserByLineIdResponse;
  return result;
}

/**
 * 依 LINE ID 取得結帳偏好（供結帳表單預填）
 */
export async function getCheckoutPreferences(
  lineId: string
): Promise<CheckoutPreferencesResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);
  const response = await apiFetch(
    `${apiBase}/api/customers/by-line-id/${encodeURIComponent(lineId)}/checkout-preferences`,
    { method: 'GET', headers }
  );
  if (!response.ok) {
    throw new Error(`取得結帳偏好失敗 (${response.status})`);
  }
  return (await response.json()) as CheckoutPreferencesResponse;
}

/**
 * 根據 LINE ID 取得使用者基本資訊
 */
export async function getUserByLineId(
  lineId: string
): Promise<UserByLineIdResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  console.log('📤 查詢使用者 (LINE ID):', {
    apiBase,
    lineId: lineId.substring(0, 10) + '...',
    tenantId,
  });

  const response = await apiFetch(
    `${apiBase}/api/customers/by-line-id/${encodeURIComponent(lineId)}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    if (response.status === 404) {
      const error = await response.json();
      const errorMessage = error.error || '找不到此 LINE ID 對應的使用者';
      // 建立一個特殊的錯誤，讓 App.tsx 可以識別這是 404 錯誤
      const notFoundError = new Error(errorMessage) as Error & {
        status: number;
      };
      notFoundError.status = 404;
      throw notFoundError;
    }
    let errorMessage = '查詢失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 查詢失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 查詢失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `查詢失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as UserByLineIdResponse;
  console.log('✅ 查詢成功:', result);
  return result;
}

/**
 * 發送手機驗證 OTP
 */
export async function sendPhoneOtp(
  phone: string,
  lineId: string
): Promise<{ success: boolean }> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const response = await apiFetch(`${apiBase}/api/customers/phone/send-otp`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ phone, lineId }),
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
    };
    throw new Error(err.message || '發送驗證碼失敗，請稍後再試');
  }

  return (await response.json()) as { success: boolean };
}

/**
 * 驗證 OTP 並完成手機驗證
 */
export async function verifyPhoneOtp(
  phone: string,
  lineId: string,
  code: string
): Promise<{ success: boolean }> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const response = await apiFetch(`${apiBase}/api/customers/phone/verify`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ phone, lineId, code }),
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
    };
    throw new Error(err.message || '驗證失敗，請檢查驗證碼');
  }

  return (await response.json()) as { success: boolean };
}

/**
 * 取得使用者詳細資訊（包含完整資料）
 */
export async function getUserDetail(
  userId: number
): Promise<UserDetailResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  console.log('📤 查詢使用者詳細資訊:', { apiBase, userId, tenantId });

  const response = await apiFetch(`${apiBase}/api/customers/${userId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    let errorMessage = '查詢失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 查詢失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 查詢失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `查詢失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as UserDetailResponse;
  console.log('✅ 查詢成功:', result);
  return result;
}

/**
 * 取得點數交易歷史
 */
export async function getPointsHistory(
  userId: number,
  page: number = 1,
  limit: number = 10
): Promise<PointsHistoryResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortDir: 'desc',
  });

  console.log('📤 查詢點數交易歷史:', {
    apiBase,
    userId,
    page,
    limit,
    tenantId,
  });

  const response = await apiFetch(
    `${apiBase}/api/customers/${userId}/points-history?${params.toString()}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    let errorMessage = '查詢失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 查詢失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 查詢失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `查詢失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as PointsHistoryResponse;
  console.log('✅ 查詢成功:', result);
  return result;
}

/** 會員電子券單筆（用於「我的電子券」） */
export interface CustomerVoucherItem {
  id: string;
  order_id: string;
  order_item_id: string;
  product_id: string;
  code: string;
  status: 'valid' | 'redeemed' | 'expired';
  applicable_date: string | null;
  applicable_time_start: string | null;
  applicable_time_end: string | null;
  redeemed_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerVouchersListResponse {
  success: boolean;
  data: CustomerVoucherItem[];
  total: number;
  timestamp: string;
}

/** 會員套票單筆（用於「我的套票」） */
export interface CustomerPackageItem {
  id: string;
  tenant_id: string;
  customer_id: string;
  product_id: string;
  product_name: string;
  initial_quantity: number;
  remaining_quantity: number;
  start_date: string;
  expiry_date: string | null;
  status: 'active' | 'finished' | 'expired';
  created_at: string;
  updated_at: string;
}

export interface CustomerPackagesListResponse {
  success: boolean;
  data: CustomerPackageItem[];
  expired_warnings?: CustomerPackageItem[];
  timestamp: string;
}

/**
 * 取得會員電子券列表（用於「我的電子券」、出示 QR）
 */
export async function getCustomerVouchers(
  customerId: string,
  options?: { includeExpired?: boolean; page?: number; limit?: number }
): Promise<CustomerVouchersListResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);
  const params = new URLSearchParams();
  if (options?.includeExpired !== undefined) {
    params.set('includeExpired', String(options.includeExpired));
  }
  if (options?.page !== undefined) params.set('page', String(options.page));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));

  const url = `${apiBase}/api/customers/${encodeURIComponent(customerId)}/vouchers${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await apiFetch(url, { method: 'GET', headers });

  if (!response.ok) {
    const err = (await response.json()).catch(() => ({})) as { error?: string };
    throw new Error(err?.error || '取得電子券列表失敗');
  }
  return response.json() as Promise<CustomerVouchersListResponse>;
}

/**
 * 取得會員套票列表（用於「我的套票」、出示 QR）
 */
export async function getCustomerPackages(
  customerId: string,
  options?: { includeExpired?: boolean }
): Promise<CustomerPackagesListResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);
  const params = options?.includeExpired ? '?includeExpired=true' : '';

  const response = await apiFetch(
    `${apiBase}/api/customers/${encodeURIComponent(customerId)}/packages${params}`,
    { method: 'GET', headers }
  );

  if (!response.ok) {
    const err = (await response.json()).catch(() => ({})) as {
      error?: string;
      message?: string;
    };
    const message =
      err?.error ?? err?.message ?? `取得套票列表失敗 (${response.status})`;
    throw new Error(message);
  }
  return response.json() as Promise<CustomerPackagesListResponse>;
}

/**
 * 取得使用者訂單列表
 */
export async function getUserOrders(
  userId: number,
  page: number = 1,
  limit: number = 10
): Promise<OrderListResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortBy: 'id',
    sortDir: 'desc',
  });

  console.log('📤 查詢訂單列表:', { apiBase, userId, page, limit, tenantId });

  // 注意：訂單路由仍在 /orders 下，使用 /api/orders 查詢特定用戶的訂單
  const response = await apiFetch(
    `${apiBase}/api/orders?user_id=${userId}&${params.toString()}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    let errorMessage = '查詢失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 查詢失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 查詢失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `查詢失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as OrderListResponse;
  console.log('✅ 查詢成功:', result);
  return result;
}

interface UpdateOrderStatusRequest {
  order_status?: OrderStatusValue;
  payment_status?: PaymentStatusValue;
  /** 確認取貨（僅面交預購）：僅更新出貨單為已取貨 */
  confirm_pickup?: boolean;
  status?: 'created' | 'confirmed' | 'paid' | 'cancelled';
}

interface UpdateOrderStatusResponse {
  success: boolean;
  data: Order;
  timestamp: string;
}

/**
 * 更新訂單狀態
 */
export async function updateOrderStatus(
  orderId: number,
  data: UpdateOrderStatusRequest
): Promise<UpdateOrderStatusResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  console.log('📤 更新訂單狀態:', { apiBase, orderId, data, tenantId });

  const response = await apiFetch(`${apiBase}/api/orders/${orderId}/status`, {
    method: 'PUT',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let errorMessage = '更新失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 更新失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 更新失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `更新失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as UpdateOrderStatusResponse;
  console.log('✅ 更新成功:', result);
  return result;
}

interface CreateUserRequest {
  name: string;
  phone: string;
  line_id: string;
  birthday?: string;
  role?: 'CLIENT' | 'ADMIN';
  is_active?: number;
}

interface CreateUserResponse {
  success: boolean;
  data: {
    id: number;
    line_id: string | null;
    name: string;
    phone: string | null;
    role: 'CLIENT' | 'ADMIN';
    is_active: number;
    points: number;
    points_yuan_equivalent: number;
    created_at: string;
    updated_at: string;
    last_purchase_at: string | null;
    current_month_spending: number;
    last_month_spending: number;
  };
  timestamp: string;
}

/**
 * 更新會員（部分欄位，供會員中心編輯電話、生日等）
 */
export async function updateCustomer(
  customerId: string,
  data: { name?: string; phone?: string | null; birthday?: string | null }
): Promise<{
  success: boolean;
  data: UserDetailResponse['data'];
  timestamp: string;
}> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const response = await apiFetch(`${apiBase}/api/customers/${customerId}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || '更新失敗');
  }

  return response.json() as Promise<{
    success: boolean;
    data: UserDetailResponse['data'];
    timestamp: string;
  }>;
}

/**
 * 建立新使用者
 */
export async function createUser(
  data: CreateUserRequest
): Promise<CreateUserResponse> {
  const apiBase = getApiBase();
  const tenantId = getTenantIdFromUrl();
  const headers = getApiHeaders(tenantId);

  const requestData = {
    name: data.name,
    phone: data.phone,
    line_id: data.line_id,
    ...(data.birthday ? { birthday: data.birthday } : {}),
    role: data.role || 'CLIENT',
    is_active: data.is_active !== undefined ? data.is_active : 1,
  };

  console.log('📤 建立使用者:', {
    apiBase,
    requestData: {
      ...requestData,
      line_id: requestData.line_id.substring(0, 10) + '...',
    },
    tenantId,
  });

  const response = await apiFetch(`${apiBase}/api/customers`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestData),
  });

  if (!response.ok) {
    let errorMessage = '註冊失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 註冊失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 註冊失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `註冊失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as CreateUserResponse;
  console.log('✅ 註冊成功:', result);
  return result;
}

export type {
  UserByLineIdResponse,
  UserDetailResponse,
  PointsTransaction,
  PointsHistoryResponse,
  Order,
  OrderListResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  CreateUserRequest,
  CreateUserResponse,
};
